import React, { useState, useEffect, useRef, useCallback } from "react";
import PropTypes from "prop-types";
import { useWindowSize } from "../utils/hooks";

import { Window, DialogActionsBar } from "@progress/kendo-react-dialogs";
import { Button } from "@progress/kendo-react-buttons";

import "./Dialog.scss";
import { useBlockerRegistration } from "../utils/BlockerProvider";

/**
 * Dialog content component
 * @module
 * @param {func} onClose - Function invoked on dialog close
 * @param {func} [onCancel] - Function invoked on cancel
 * @param {func} [onClick] - Function invoked on sumbit button click
 * @param {element} children - Children content of dialog
 * @param {string} title - Title text of dialog
 * @param {bool} [fullscreen] - Full screen dialog
 * @param {bool} [disabled] - Is submit button disabled?
 * @param {bool} [hideButtons] - Hide buttons
 * @param {bool} [hideCancelBtn] - Hide cancel button
 * @param {sting} [form] - Form
 * @param {string} [confirmText] - Confirm text
 * @param {string} [cancelText] - Cancel text
 * @param {number} relativeLeft - Window relative left
 * @param {number} relativeTop - Window relative top  
 * @param {number} level - Window level
 */
const DialogContent = (props) => {
  const { onClose } = props
  const [top, setTop] = useState(undefined);
  const [left, setLeft] = useState(undefined);
  const [moveWidth, setMoveWidth] = useState(undefined); //fix handleMove width
  const windowSize = useWindowSize();

  const blockerFunction = useCallback(() => { onClose() }, [onClose])
  const blockerCondition = useCallback(() => true, [])
  useBlockerRegistration(blockerCondition, blockerFunction)

  const dialogRef = useRef(null);

  useEffect(() => {
    //set dialog to center after resize
    if (windowSize.width) {
      setTop(undefined);
      setLeft(undefined)
    }
  }, [windowSize])

  //test
  useEffect(() => {
    if (dialogRef.current && dialogRef.current.element && props.name) {
      dialogRef.current.element.setAttribute('data-testid', props.name);
    }
  }, [props.name])

  const handleMove = (event) => {
    if (!moveWidth) {
      setMoveWidth(event.target.windowElement.offsetWidth)
    }
    if (event.left || event.top) {
      const maxLeft = window.innerWidth - (event.target.windowElement.offsetWidth / 2);
      const minLeft = event.target.windowElement.offsetWidth / 2;
      const maxTop = window.innerHeight - event.target.windowElement.offsetHeight;

      const newLeft = event.left > maxLeft ? maxLeft : (event.left < minLeft ? minLeft : event.left);
      const newTop = event.top > maxTop ? maxTop : event.top;
      setLeft(newLeft - (props.level ? props.level * 30 : 0));
      setTop(newTop - (props.level ? props.level * 30 : 0));
      if (event.end) {
        setMoveWidth(undefined)
      }
    }
  };

  const width = moveWidth || props.width || (props.fullscreen ? window.innerWidth * 90 / 100 : "none");

  return (
    <Window
      className={props.className}
      title={props.title}
      onClose={onClose}
      draggable={true}
      resizable={false}
      modal={true}
      doubleClickStageChange={false}
      stage="DEFAULT"
      onMove={handleMove}
      minimizeButton={() => null}
      maximizeButton={() => null}
      left={(left === undefined ? window.innerWidth / 2 : left) + (props.level ? props.level * 30 : 0)}
      top={(top === undefined ? window.innerHeight * 10 / 100 : top) + (props.level ? props.level * 30 : 0)}
      shouldUpdateOnDrag={false}
      ref={dialogRef}
      style={{ width, height: "none", transform: `translate(-50%, 0)` }}
    >
      <div className="dialog-content">
        {props.children}
      </div>
      {props.hideButtons || props.fullscreen
        ? false
        : true && (
          <DialogActionsBar layout={"stretched"}>
            {props.hideCancelBtn ? null : (
              <Button
                onClick={props.onCancel ? props.onCancel : onClose}
                disabled={props.cancelDisabled}
                rounded={null}
                key={1}
              >
                {props.cancelText || "Zrušiť"}
              </Button>
            )}
            {(props.onMiddleClick !== undefined && props.middleText !== undefined) ? (
              <Button
                onClick={props.onMiddleClick}
                rounded={null}
                key={3}
                className="divider"
              >
                {props.middleText}
              </Button>) : null
            }
            <Button
              form={props.form}
              type={props.form ? "submit" : null}
              themeColor={"primary"}
              rounded={null}
              onClick={props.onClick}
              disabled={props.disabled}
              key={2}
            >
              {props.confirmText || "OK"}
            </Button>
          </DialogActionsBar>
        )}
    </Window>
  );
};

DialogContent.propTypes = {
  onClose: PropTypes.func.isRequired,
  onCancel: PropTypes.func,
  onClick: PropTypes.func,
  onMiddleClick: PropTypes.func,
  children: PropTypes.element.isRequired,
  title: PropTypes.string.isRequired,
  fullscreen: PropTypes.bool,
  disabled: PropTypes.bool,
  hideButtons: PropTypes.bool,
  hideCancelBtn: PropTypes.bool,
  form: PropTypes.string,
  confirmText: PropTypes.string,
  middleText: PropTypes.string,
  cancelText: PropTypes.string,
  relativeTop: PropTypes.number.isRequired,
  relativeLeft: PropTypes.number.isRequired,
  level: PropTypes.number,
};

export default DialogContent;
